import { get } from 'lodash';
import moment from 'moment';
import { createSelector } from 'reselect';
import {
  getDateOfBirth,
  getUserBusinessPartyId,
} from '../user/PersonalInformation/personalInformation.selectors';
import { getExistingBanks } from '../existingBanks/existingBanks.selectors';
import { getUserRetirementFunds } from '../funds/UserFunds/userFunds.selectors';
import {
  RMD_ELIGIBLE_AGE,
  YEAR_1949,
  YEAR_1950,
  YEAR_2021,
  YEAR_2022,
} from '../../../settings/settings';

export const getIsRMDDisclaimerAccepted = (state) =>
  state.RMDSetting.disclaimerAccepted;

export const getExistingRMD = (state) => {
  return get(state, 'RMDSetting.existingRMDData') || {};
};

export const getExistingRMDSetting = (state) => {
  return get(state, 'RMDSetting') || {};
};

/**
 * Simple selector to get RMD eligibility status in personal info data
 * @param getPersonalInformationData
 * @returns {Boolean}
 */
export const getIsRMDEligible = createSelector(
  [getExistingRMDSetting],
  (rmdInfo) => get(rmdInfo, 'rmdLimitsData.rmdSummary.eligibleForRMD', false)
);

/**
 * Selector to get RMD March/April Deadline - when user turns 72 in current year
 * @param getExistingRMDSetting
 * @returns {Boolean}
 */
export const getFirstRMDDeadline = createSelector(
  [getExistingRMDSetting],
  (rmdInfo) => get(rmdInfo, 'rmdLimitsData.rmdSummary.firstRMDDeadline')
);

/**
 * Selector to get RMD last bussiness day Deadline
 * @param getExistingRMDSetting
 * @returns {Boolean}
 */
export const getLastBusinessDayOfYear = createSelector(
  [getExistingRMDSetting],
  (rmdInfo) => get(rmdInfo, 'rmdLimitsData.rmdSummary.lastBusinessDayOfYear')
);

export const getTotalRMDAmount = (state) => {
  const totalRMDAmount = get(
    state,
    'RMDSetting.rmdLimitsData.rmdSummary.totalRMD'
  );
  return typeof totalRMDAmount === 'number' ? totalRMDAmount : 0;
};

export const getTotalPendingRMDAmount = (state) =>
  (!isNaN(state.RMDSetting.rmdLimitsData.totalPendingRMD) &&
    state.RMDSetting.rmdLimitsData.totalPendingRMD) ||
  0;

export const getTotalSystematicRMDAmount = (state) =>
  (!isNaN(state.RMDSetting.rmdLimitsData.totalPendingSystematicRMD) &&
    state.RMDSetting.rmdLimitsData.totalPendingSystematicRMD) ||
  0;

export const getRMDAvailableBalance = (state) => {
  const total = get(
    state,
    'RMDSetting.rmdLimitsData.rmdSummary.totalRemainingYear'
  );
  return typeof total === 'number' ? total : 0;
};

export const getSystematicLE = (state) =>
  (!isNaN(state.RMDSetting.rmdLimitsData.hasSystematicLE) &&
    state.RMDSetting.rmdLimitsData.hasSystematicLE) ||
  false;

export const getSystematicActivityRMDs = (state) =>
  state.RMDSetting.rmdLimitsData.systematicActivityRMDs
    ? state.RMDSetting.rmdLimitsData.systematicActivityRMDs
    : [];

export const getRMDAgreements = (state) =>
  state.RMDSetting.existingRMDData?.rmdAgreements
    ? state.RMDSetting.existingRMDData.rmdAgreements
    : [];

export const getExistingRMDSWithAgreementParties = createSelector(
  getSystematicActivityRMDs,
  getUserRetirementFunds,
  (existingRMDinfo, userRetirementFunds) => {
    if (existingRMDinfo !== null && existingRMDinfo !== undefined) {
      existingRMDinfo.forEach((agreement) => {
        const rmdAgreementID = agreement.sourceAgreementKey.agreementID;
        if (userRetirementFunds.TRADITIONAL_IRA !== undefined) {
          userRetirementFunds.TRADITIONAL_IRA.forEach((fund) => {
            if (fund.agreementID === rmdAgreementID) {
              Object.assign(agreement, fund);
            }
          });
        }
        if (userRetirementFunds.ROLLOVER_IRA !== undefined) {
          userRetirementFunds.ROLLOVER_IRA.forEach((fund) => {
            if (fund.agreementID === rmdAgreementID) {
              Object.assign(agreement, fund);
            }
          });
        }
      });
    }
    return existingRMDinfo;
  }
);
export const getExistingRMDTotalRMD = createSelector(
  getTotalRMDAmount,
  (existingRMDinfo) => {
    return existingRMDinfo;
  }
);

export const getOnlyOWNRRMDs = createSelector(
  getExistingRMDSWithAgreementParties,
  getUserBusinessPartyId,
  (rmds, BPID) => {
    return rmds?.filter((agreements) => {
      if (agreements?.agreementParties) {
        const ownerArr = agreements?.agreementParties.filter(
          (roles) => roles.roleCode === 'OWNR'
        );
        return ownerArr.length > 0
          ? ownerArr[0].businessPartyID === BPID
          : false;
      }
      return false;
    });
  }
);
export const doesEverySystematicHaveAnAgreement = createSelector(
  getOnlyOWNRRMDs,
  getRMDAgreements,
  (systematic, agreement) => {
    const systematicMatchAgreement = systematic.map((sys) => {
      return agreement.some((fund) => {
        return sys.agreementID === fund.agreementID;
      });
    });
    return systematicMatchAgreement.every((answer) => answer);
  }
);
// TODO: Uncomment when needed, is for future use, commented out to avoid linter warnings, Sam 12/6/18
// export const isInRMDPostYear = createSelector(
//   getDateOfBirth,
//   dateOfBirth => {
//     const todaysDate = new Date(getCurrentDate().format('MM/DD/YY'));
//     const customerDob = new Date(dateOfBirth);
//     const durationInMilli = todaysDate - customerDob;
//     const yearsOfAge = moment.duration(durationInMilli, 'milliseconds').asYears();
//     return false;
//   }
// );
export const getYearUserTurningRMDEligible = createSelector(
  getDateOfBirth,
  (dateOfBirth) => {
    const yearBorn = moment(dateOfBirth).utc().format('YYYY');
    if (yearBorn === YEAR_1950.toString()) {
      return YEAR_2022.toString();
    } else if (yearBorn === YEAR_1949.toString()) {
      return YEAR_2021.toString();
    } else {
      const dateAtUserTurnsRMDEligible = moment(dateOfBirth).add(
        RMD_ELIGIBLE_AGE,
        'years'
      );
      return moment(dateAtUserTurnsRMDEligible).utc().format('YYYY');
    }
  }
);

export const getFundsByAgreementID = (state) =>
  state.userFunds.fundsByAgreementID;

export const getCurrentFunds = createSelector(
  getSystematicActivityRMDs,
  getFundsByAgreementID,
  getExistingBanks,
  (systematicActivityRMDs, fundsByAgreementId, existingBanks) => {
    const uniqKeys =
      systematicActivityRMDs?.reduce((acc, val) => {
        acc[val.sourceAgreementKey.uniqueKey] = val;
        return acc;
      }, {}) || {};
    const result = Object.values(fundsByAgreementId || {})
      .filter((val) => {
        return uniqKeys[val.uniqueKey];
      })
      .map((fund) => {
        const bank = existingBanks.filter(
          (bank) =>
            bank.bankAgreementID ===
            uniqKeys[fund.uniqueKey]?.bankAgreementKey?.bankAgreementID
        );
        return {
          ...fund,
          disbursementType: fund.disbursementType || {},
          deliveryType: uniqKeys[fund.uniqueKey]?.deliveryType,
          transferDays: uniqKeys[fund.uniqueKey]?.transferDays,
          IRAWithholding: uniqKeys[fund.uniqueKey]?.IRAWithholding,
          currentYearAmount: uniqKeys[fund.uniqueKey]?.amount,
          transferMonths:
            uniqKeys[fund.uniqueKey]?.transfers
              ?.filter((tr) => tr.status.enumerationName === 'on')
              .map((tr) => tr.month) || [],
          deferMonths:
            uniqKeys[fund.uniqueKey]?.transfers
              ?.filter((tr) => tr.status.enumerationID === 30)
              .map((tr) => tr.month) || [],
          bank,
        };
      });
    return result;
  }
);

export const getRMDSummary = (state) =>
  state.RMDSetting ? state.RMDSetting.rmdLimitsData : {};

export const getIsRMDBalanceAndAmountIsZero = createSelector(
  getRMDSummary,
  (rmdLimitsData) => {
    if (
      rmdLimitsData &&
      rmdLimitsData.rmdAgreements &&
      rmdLimitsData.rmdAgreements.length > 0
    ) {
      return rmdLimitsData.rmdAgreements.some(
        (agreement) => agreement.balance === 0 && agreement.rmdamount === 0
      );
    }
    return false;
  }
);

export const getShowDashboard = (state) =>
  state.RMDSetting?.rmdLimitsData?.showDashboard !== undefined
    ? state.RMDSetting.rmdLimitsData.showDashboard
    : true;
